import { DSButtonV2 } from '@elliemae/ds-button';
import { DSInputText } from '@elliemae/ds-controlled-form';
import { DSInputMask, MASK_TYPES } from '@elliemae/ds-form';
import { DSFormLayoutBlockItem } from '@elliemae/ds-form-layout-blocks';
import { DSSingleComboBox } from '@elliemae/ds-form-single-combobox';
import { Grid } from '@elliemae/ds-grid';
import { Delete } from '@elliemae/ds-icons';
import { DSModal, MODAL_SUB_TYPE_V2, MODAL_TYPE_V2 } from '@elliemae/ds-modal';
import { DSSeparator } from '@elliemae/ds-separator';
import { bool, func, number, oneOfType, shape, string } from 'prop-types';
import { useMemo, useState } from 'react';
import { useIsZeroBasedPricing } from '../../../../common/utils/customHooks/useIsZeroBasedPricing';
import { parseDecimals } from './helpers';
import { StyledCard } from './styled';
import { useDescription } from '../../../../common/utils/customHooks/useDescription';

export const AdjustmentCard = ({
  index,
  rowCard,
  onChange,
  onDeleteRow,
  errors,
}) => {
  const {
    paidByOptions,
    adjustmentReasonsOptions,
    selectedPaidByValue,
    setSelectedPaidByValue,
    selectedAdjustmentReasonValue,
    setSelectedAdjustmentReasonValue,
    rowCard: updatedRowCard,
  } = useDescription(rowCard);

  const [openModal, setOpenModal] = useState(false);

  const isCustomPriceAdjustment = useMemo(
    () => updatedRowCard.adjustmentType === 'CustomPriceAdjustment',
    [updatedRowCard],
  );
  const isCustomRateAdjustment = useMemo(
    () => updatedRowCard.adjustmentType === 'CustomRateAdjustment',
    [updatedRowCard],
  );
  const isZeroBasedPricing = useIsZeroBasedPricing();

  return (
    <>
      <StyledCard>
        <Grid alignItems="center" gutter={5}>
          <div style={{ textAlign: 'right' }}>
            <DSButtonV2
              buttonType="icon"
              onClick={() => setOpenModal(true)}
              size="l"
              aria-label="Delete rowCard"
            >
              <Delete size="m" color={['brand-primary', 600]} />
            </DSButtonV2>
          </div>
        </Grid>
        <Grid gutter={5} alignItems="flex-start">
          <div
            style={{
              display: 'flex',
              gap: '30px',
              alignItems: 'flex-start',
              marginBottom: '15px',
            }}
          >
            <DSFormLayoutBlockItem
              style={{ flexGrow: 1 }}
              label="Adjustment Name"
              inputID="nameAdjustment"
              required
              hasError={![undefined, null, ''].includes(errors?.description)}
              validationMessage={errors?.description}
              withHighlight={Boolean(errors?.description)}
            >
              <DSInputText
                id="nameAdjustment"
                placeholder=""
                value={updatedRowCard.description}
                onChange={({ target: { value } }) =>
                  onChange(value, 'description', index)
                }
                maxLength={256}
              />
            </DSFormLayoutBlockItem>
            {!isCustomRateAdjustment && (
              <DSFormLayoutBlockItem
                style={{ flexGrow: 1 }}
                label="Price"
                inputID="priceAdjustment"
                hasError={![undefined, null, ''].includes(errors?.price)}
                validationMessage={errors?.price}
                withHighlight={Boolean(errors?.price)}
                required={Boolean(!isCustomRateAdjustment)}
                feedbackMessage={`Par pricing is ${
                  isZeroBasedPricing ? `0.00` : `100.00`
                }`}
              >
                <DSInputMask
                  mask={(num) =>
                    MASK_TYPES.NUMBER({
                      prefix: '',
                      integerLimit: isCustomPriceAdjustment ? 2 : 1,
                      allowDecimal: true,
                      allowNegative: true,
                      decimalLimit: 3,
                    })(num)
                  }
                  name="price"
                  value={updatedRowCard.price}
                  placeholder=""
                  labelText="Price"
                  onChange={({ target: { value } }) => {
                    onChange(value, 'price', index);
                  }}
                  onBlur={({ target: { value } }) => {
                    onChange(parseDecimals(value, 3), 'price', index);
                  }}
                />
              </DSFormLayoutBlockItem>
            )}
            {isCustomRateAdjustment && (
              <DSFormLayoutBlockItem
                style={{ flexGrow: 1 }}
                label="Rate"
                inputID="rateAdjustment"
                hasError={![undefined, null, ''].includes(errors?.rate)}
                validationMessage={errors?.rate}
                withHighlight={Boolean(errors?.rate)}
              >
                <DSInputMask
                  mask={(num) =>
                    MASK_TYPES.NUMBER({
                      prefix: '',
                      integerLimit: 2,
                      allowDecimal: true,
                      allowNegative: true,
                      decimalLimit: 3,
                    })(num)
                  }
                  name="rate"
                  value={updatedRowCard.rate}
                  placeholder=""
                  labelText="Rate"
                  onChange={({ target: { value } }) => {
                    onChange(value, 'rate', index);
                  }}
                  onBlur={({ target: { value } }) => {
                    onChange(parseDecimals(value, 3), 'rate', index);
                  }}
                />
              </DSFormLayoutBlockItem>
            )}
          </div>
        </Grid>
        <Grid cols={['1fr', '1fr']} gutter={5} alignItems="flex-start">
          {paidByOptions?.length ? (
            <DSFormLayoutBlockItem
              mb="xs"
              label="Paid By"
              inputID="adjustment_paid_by"
              hasError={![undefined, null, ''].includes(errors?.paidByGuid)}
              validationMessage={errors?.paidByGuid}
              withHighlight={Boolean(errors?.paidByGuid)}
              required
            >
              <DSSingleComboBox
                placeholder="Select the paid by category"
                allOptions={paidByOptions}
                onChange={(e) => {
                  const value = e?.value === null ? undefined : e?.value;
                  onChange(value, 'paidByGuid', index);
                  setSelectedPaidByValue(e ?? {});
                }}
                selectedValue={selectedPaidByValue}
                id="adjustment_paid_by"
              />
            </DSFormLayoutBlockItem>
          ) : (
            <div></div>
          )}
          {adjustmentReasonsOptions?.length ? (
            <DSFormLayoutBlockItem
              mb="xs"
              label="Adjustment Reason"
              inputID="adjustment_reason"
              hasError={
                ![undefined, null, ''].includes(errors?.adjustmentReasonGuid)
              }
              validationMessage={errors?.adjustmentReasonGuid}
              withHighlight={Boolean(errors?.adjustmentReasonGuid)}
              required
            >
              <DSSingleComboBox
                placeholder="Select the adjustment reason"
                allOptions={adjustmentReasonsOptions}
                onChange={(e) => {
                  const value = e?.value === null ? undefined : e?.value;
                  onChange(value, 'adjustmentReasonGuid', index);
                  setSelectedAdjustmentReasonValue(e ?? {});
                }}
                selectedValue={selectedAdjustmentReasonValue}
                id="adjustment_paid_by"
              />
            </DSFormLayoutBlockItem>
          ) : (
            <div></div>
          )}
        </Grid>
        <DSSeparator dashed style={{ marginBottom: `0`, marginTop: `0` }} />
      </StyledCard>
      <DSModal
        centered
        confirmLabel="Ok"
        isOpen={openModal}
        modalSubType={MODAL_SUB_TYPE_V2.WARNING}
        modalType={MODAL_TYPE_V2.DECISION}
        onReject={() => setOpenModal(false)}
        onClose={() => setOpenModal(false)}
        onConfirm={() => {
          onDeleteRow(index);
          setOpenModal(false);
        }}
        rejectLabel="Cancel"
        showClose
        showRejectButton
        version={2}
      >
        <p>Do you want to delete this entry?</p>
      </DSModal>
    </>
  );
};

AdjustmentCard.defaultProps = {
  index: 0,
  rowCard: {
    price: '',
    name: '',
    persist: true,
    persistDisabled: false,
    adjustmentType: '',
  },
  errors: {
    hasError: false,
    messages: [''],
  },
};

AdjustmentCard.propTypes = {
  onChange: func.isRequired,
  onDeleteRow: func.isRequired,
  index: number,
  rowCard: shape({
    name: string,
    price: oneOfType([number, string]),
    persist: bool,
    persistDisabled: bool,
    adjustmentType: string,
  }),
  errors: shape({
    hasError: false as any,
    messages: [] as any,
  }),
};
